import { Plus, Trash2 } from 'lucide-react';
import {
  FieldArrayWithId,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ContactHandleType } from '@/services/types/treatment-types';

import { ProviderFormData } from './types';

interface ContactInfoTabProps {
  register: UseFormRegister<ProviderFormData>;
  setValue: UseFormSetValue<ProviderFormData>;
  watch: UseFormWatch<ProviderFormData>;
  handleFields: FieldArrayWithId<ProviderFormData, 'handles', 'id'>[];
  appendHandle: (value: any) => void;
  removeHandle: (index: number) => void;
}

export const ContactInfoTab = ({
  register,
  setValue,
  watch,
  handleFields,
  appendHandle,
  removeHandle,
}: ContactInfoTabProps) => {
  const addContactHandle = () => {
    appendHandle({
      value: '',
      handle_type: ContactHandleType.PHONE,
      name: '',
      sources: [],
    });
  };

  return (
    <div className="space-y-6">
      {/* Address */}
      <div className="space-y-2">
        <Label htmlFor="address" className="text-sm font-medium text-gray-900">
          Address
        </Label>
        <Textarea
          id="address"
          {...register('address')}
          placeholder="Enter the provider's address"
          className="w-full min-h-[80px]"
        />
        <p className="text-xs text-gray-500">
          Physical address of the medical provider
        </p>
      </div>

      {/* Contact Handles */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-gray-900">
            Contact Information
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addContactHandle}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Contact
          </Button>
        </div>

        {handleFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
            <p className="text-sm">No contact information added yet</p>
            <p className="text-xs mt-1">
              Add phone numbers, emails, websites, or other contact methods
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {handleFields.map((field, index) => (
              <div key={field.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Contact Type */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">
                      Type
                    </Label>
                    <Select
                      value={
                        watch(`handles.${index}.handle_type`) ||
                        ContactHandleType.PHONE
                      }
                      onValueChange={(value) =>
                        setValue(
                          `handles.${index}.handle_type`,
                          value as ContactHandleType,
                        )
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ContactHandleType.PHONE}>
                          Phone
                        </SelectItem>
                        <SelectItem value={ContactHandleType.EMAIL}>
                          Email
                        </SelectItem>
                        <SelectItem value={ContactHandleType.FAX}>
                          Fax
                        </SelectItem>
                        <SelectItem value={ContactHandleType.WEBSITE}>
                          Website
                        </SelectItem>
                        <SelectItem value={ContactHandleType.MAILING_ADDRESS}>
                          Mailing Address
                        </SelectItem>
                        <SelectItem value={ContactHandleType.OTHER}>
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contact Value */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">
                      Contact Value
                    </Label>
                    <Input
                      {...register(`handles.${index}.value`)}
                      placeholder="Enter contact information"
                      className="w-full"
                    />
                  </div>

                  {/* Description/Name */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">
                      Description
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        {...register(`handles.${index}.name`)}
                        placeholder="e.g., Main Office, Billing"
                        className="w-full"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHandle(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Phone numbers, emails, websites, and other ways to contact this
          provider
        </p>
      </div>
    </div>
  );
};
